import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { callAPI, extractLabels } from "../components/utils";
import Table from "../components/table/table";
import SearchBox from "../components/table/searchBox";
import PaginationControls from "../components/table/pagination";
import Tag from '../components/ui/tags';
import "../css/assignTutors.css";
import PageLayout from '../components/layouts/pageLayout';
import {Box, Divider, Stack, Typography} from '@mui/material';
import Dropdown from "../components/ui/dropdown";
import TextInput from "../components/ui/textInput";
import Button from "../components/ui/button";


const AssignTutorsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student || null;

  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [allTutors, setAllTutors] = useState([]);
  const [filteredTutors, setFilteredTutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTutor, setCurrentTutor] = useState("None");
  const [error, setError] = useState(false);
  const [tutorAssociations, setTutorAssociations] = useState([]);
  const [staticData, setStaticData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalStudent, setModalStudent] = useState(null);

  useEffect(() => {
    if (!student) return;

    const processSubjects = () => {
      const validSubjects = Array.isArray(student.junctions.subjects)
        ? student.junctions.subjects.filter(({ label, value }) => label && value).map(({ label, value, data }) => ({ label, value, data }))
        : [];

      setSubjects(validSubjects);
      setSelectedSubject(validSubjects[0]?.value || "");
    };

    const fetchTutors = async () => {
      setLoading(true);
      try {
        const { data: tutorData } = await callAPI({
          request: "database/admin/getEntities",
          method: "post",
          params: {
            "entityType": "tutors",
          }
        });

        setStaticData(tutorData.staticData)
        setAllTutors(tutorData?.data || []);

        const { data: tutorAssociations } = await callAPI({
          request: "database/tutor/getTutorRelations",
          method: "get",
        });
        
        setTutorAssociations(tutorAssociations?.data || []);
      } catch (error) {
        setError(true);
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };

    processSubjects();
    fetchTutors();
  }, [student]);

  useEffect(() => {
    if (!selectedSubject || !allTutors.length || !tutorAssociations.length) return;

    const filtered = allTutors.filter((tutor) => {
      const matchesSubject = tutor.subjects.some(({ value }) => String(value) === String(selectedSubject));
      const matchesSearch = searchTerm ? tutor.name.value.toLowerCase().includes(searchTerm.toLowerCase()) : true;
      return matchesSubject && matchesSearch;
    });
    setFilteredTutors(filtered);

    const tutorAssociation = tutorAssociations?.find((tutorData) =>
      tutorData?.students?.some((studentEntry) =>
        String(studentEntry?.student?.value) === String(student.personalInfo.id.value) &&
        String(studentEntry?.student?.data?.subject) === String(selectedSubject)
      )
    );
  
    if (tutorAssociation) {
      const tutor = allTutors.find((tutor) => tutor.id.value === tutorAssociation.tutorId);
      if (tutor) {
        setCurrentTutor(tutor.name.value);
      }
    } else {
      setCurrentTutor("None");
    }
  }, [selectedSubject, searchTerm, allTutors, tutorAssociations]);

  const handleAssignTutor = async (tutor) => {
    try {
      await callAPI({
        request: "tutors/assignTutor",
        method: "post",
        params: { studentId: student?.personalInfo?.id?.value, tutorId: tutor.id, subjectId: String(selectedSubject)},
      });

      window.location.reload();
    } catch (error) {
      console.error("Error assigning tutor:", error);
      alert("Failed to assign tutor. Please try again.");
    }
  };

  const handleStudentClick = (student) => {
    setModalStudent(student);
  };

  const closeModal = () => {
    setModalStudent(null);
  };

  const totalPages = Math.ceil(filteredTutors.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedTutors = rowsPerPage === "All" ? filteredTutors : filteredTutors.slice(startIndex, startIndex + rowsPerPage);

  if (!student) return <div><h1>Error: No Student Data</h1><button onClick={() => navigate("/")}>Go to Home</button></div>;

  if (error) return <div><h1>An error occurred while loading</h1><button onClick={() => navigate("/")}>Go to Home</button></div>;

  return (
    <PageLayout title={`Assign Tutors to ${student.personalInfo.name.value}`} showBackButton>
      <Stack>
      <Typography
        variant="body1"
        sx={{
          textAlign: 'left',
          fontWeight: '500',
          marginBottom: 0.5,
        }}
      >Current Assigned Tutors</Typography>
      <Typography variant="body1">
        {currentTutor}
      </Typography>
      </Stack>

<Divider sx={{bgColor: 'var(--gray-300'}}/>      <Stack spacing={2} direction="row" alignItems="center">
        <Dropdown label="Subjects" value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)} options={subjects} boxSx={{width: '400px'}}/>
        <SearchBox label="Tutor Name" searchTerm={searchTerm} onSearch={setSearchTerm} boxSx={{width: '400px'}}/>
      </Stack>

      {loading ? <p>Loading tutors...</p> : paginatedTutors.length ? (
        <Table
          data={extractLabels(paginatedTutors)}
          columns={[
            { accessor: "name", header: "Name" },
            { accessor: "gender", header: "Gender" },
            {
              accessor: "students",
              header: "Students",
              render: (row) => {
                const tutorStudents =
                  tutorAssociations.find((assoc) => assoc.tutorId === row.id)?.students || [];
            
                return tutorStudents.length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                  {tutorStudents.map((studentData) => {
                    const subjectLabels = String(studentData.student.data.subject)
                      .split(",")
                      .map((subjectId) => {
                        const subject = staticData.subjects.find(
                          (assoc) => String(assoc.value) === subjectId.trim()
                        );
                        return subject?.label || "Unknown subject";
                      })
                      .join(", ");
            
                    return (
                      <Button
                        key={studentData.student.value}
                        onClick={() => handleStudentClick(studentData.student)}
                        variant="outlined"
                        size="small"
                        style={{
                          fontWeight: '500',
                          padding: '5px 10px',
                        }}
                      >
                        {studentData.student.label} ({subjectLabels})
                      </Button>
                    );
                  })} </Box>
                ) : (
                  "None"
                );
              },
            },
            { accessor: "level", header: "Level" },
            { accessor: "curriculums", header: "Curriculums" },
            { accessor: "capacity", header: "Capacity" },
            {accessor: "subjects", header: "Subjects" },
            {
              accessor: "action",
              header: "Actions",
              render: (row) => {
                const isActiveTutor = currentTutor === row.name;
                return (
                  <Button 
                    variant="contained"
                    size="small"
                    onClick={() => handleAssignTutor(row)} 
                    disabled={isActiveTutor}
                  >
                    {isActiveTutor ? "Assigned" : "Assign"}
                  </Button>
                );
              },
            },
          ]}
          noDataMsg="No tutors found for the selected subject."
        />
      ) : (
        <p>No tutors found for the selected subject.</p>
      )}

<Box display="flex" justifyContent="flex-end">
      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onPreviousPage={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
        onNextPage={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
        onRowsPerPageChange={(value) => setRowsPerPage(value === "All" ? "All" : Number(value))}
      />
      </Box>

      {modalStudent && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "8px",
              width: "400px",
              textAlign: "center",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <h2>Student Details</h2>
            <p>Name: {modalStudent.label}</p>
            <p>Subject: {modalStudent.data.subject}</p>
            <p>Other Info: {JSON.stringify(modalStudent.data)}</p>
            <button
              onClick={closeModal}
              style={{
                marginTop: "20px",
                padding: "10px 20px",
                background: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default AssignTutorsPage;
